import React, { useEffect, useState, useCallback, useMemo } from 'react';
import _ from 'lodash';

import { Box, Drawer, ThemeProvider } from '@kufox/mui';
import { CloseOutlined } from '@kufox/icons';
import { styled, isPropValid } from '@kufox/mui/emotion';
import { useTheme } from '@kufox/mui/hooks';

import { Tabs, HumanCaptcha } from '../components';
import remoteEvent from '@tools/remoteEvent';
import uniqueModel from '@tools/uniqueModel';
import { extendModel } from '@utils';
import { useDispatch, useSelector } from 'react-redux';
import useConstructor from '@hooks/useConstructor';

import { FORGET_EMAIL_KEY, FORGET_PHONE_KEY, NAMESPACE } from './constants';

import Layout from '../Layout';
import ForgetEmailPwd from './ForgetEmailPwd';
import ForgetPhonePwd from './ForgetPhonePwd';
import ResetPwd from './ResetPwd';
import { useLang } from '../hookTool';
import forgetPwdModel from './model';

const { withLayout } = Layout;

const Title = styled.h3`
  font-size: 34px;
  line-height: 48px;
  margin-bottom: 40px;
  colors: ${({ theme }) => theme.colors.text};
`;

const CloseIcon = styled(Box, {
  shouldForwardProp: (props) => isPropValid(props),
})(({ theme }) => {
  return {
    width: '48px',
    height: '48px',
    backgroundColor: theme.colors.text20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '100%',
    cursor: 'pointer',
  };
});
const CloseBox = styled(Box, {
  shouldForwardProp: (props) => isPropValid(props),
})(() => {
  return {
    position: 'absolute',
    top: 24,
    right: 24,
  };
});

function ForgetPwd(props = {}) {
  useConstructor(() => {
    remoteEvent.emit(remoteEvent.evts.GET_DVA, (dva, m) => {
      uniqueModel(dva, extendModel(m, forgetPwdModel));
    });
  });
  const { onSuccess, tabKey, moreClasses = {}, BoxProps = {} } = props;
  const { t } = useLang();
  const dispatch = useDispatch();
  const classes = useMemo(() => {
    return { ...moreClasses };
  }, [moreClasses]);

  const { bizType, isCodeRegx } = useSelector((state) => state[NAMESPACE]);

  const [_tabKey, setTabKey] = useState(tabKey || FORGET_PHONE_KEY);

  useEffect(() => {
    return () => {
      dispatch({ type: `${NAMESPACE}/resetInit` });
    };
  }, []);

  const handleTabChange = useCallback(
    (key) => {
      setTabKey(key);
    },
    [_tabKey],
  );

  const tabs = {
    [FORGET_PHONE_KEY]: {
      title: t(FORGET_PHONE_KEY),
      render: (props = {}) => {
        return <ForgetPhonePwd {...props} />;
      },
    },
    [FORGET_EMAIL_KEY]: {
      title: t(FORGET_EMAIL_KEY),
      render: (props = {}) => {
        return <ForgetEmailPwd {...props} />;
      },
    },
  };

  return (
    <>
      <Box className={classes.forgetPwdRoot} width={480} {...BoxProps}>
        <Title className={classes.title}>{t('forget.pwd')}</Title>
        {isCodeRegx ? (
          <ResetPwd onSuccess={onSuccess} />
        ) : (
          <>
            <Tabs defaultKey={_tabKey} onChange={handleTabChange}>
              {_.map(tabs, ({ title }, key) => {
                return <Tabs.TabPane key={key} title={title} />;
              })}
            </Tabs>
            {tabs[_tabKey].render({})}
          </>
        )}
      </Box>
      <HumanCaptcha bizType={bizType} namespace={NAMESPACE} />
    </>
  );
}

const WithThemeForgetPwd = (props) => {
  return (
    <ThemeProvider theme={props.theme || 'light'}>
      <ForgetPwd {...props} />
    </ThemeProvider>
  );
};

const LayOutForgetPwd = withLayout(WithThemeForgetPwd);

const ForgetPwdWithThemeDrawer = (props = {}) => {
  const { onClose, BoxProps = {}, tabKey, moreClasses, onSuccess, ...restProps } = props;

  const dispatch = useDispatch();

  const theme = useTheme();

  const handleClose = useCallback(() => {
    dispatch({
      type: `${NAMESPACE}/reset`,
    });
    if (onClose) {
      onClose();
    }
  }, []);

  return (
    <ThemeProvider>
      <Drawer {...restProps} onClose={handleClose}>
        <CloseBox onClick={handleClose}>
          <CloseIcon theme={theme}>
            <CloseOutlined size={24} />
          </CloseIcon>
        </CloseBox>
        <ForgetPwd
          moreClasses={moreClasses}
          onSuccess={onSuccess}
          tabKey={tabKey}
          BoxProps={{ width: 528, p: 8, pb: 5, height: '100%', ...BoxProps }}
        />
      </Drawer>
    </ThemeProvider>
  );
};

export const ForgetPwdDrawer = (props) => {
  return (
    <ThemeProvider theme={props.theme || 'light'}>
      <ForgetPwdWithThemeDrawer {...props} />
    </ThemeProvider>
  );
};

export default LayOutForgetPwd;
